import { Button, ButtonProps, Divider, Text } from "@flat-ui";
import { Stack } from "@mui-wrapper";

export interface Props extends Pick<
  ButtonProps,
  "onClick" | "isLoading" | "disabled" | "sx" | "icon"
> {
  /** Text to show inside inside the button */
  label?: string;

  /** The sub title for the provider */
  description?: string;
}

/** Renders a button for an SSO authentication method */
export function FaroButtonSSO({
  label,
  icon,
  description,
  sx,
  ...rest
}: Props): JSX.Element {
  return (
    <>
      <Button
        data-testid="sso-button-provider"
        variant="secondary"
        title={label}
        disableRipple={true}
        disableElevation={true}
        disableTouchRipple={true}
        sx={{
          ...sx,
          // The `secondary` variant tints the text and the border with the primary color
          // while hovered, pressed and disabled. Those states are listed explicitly because
          // they are more specific than the idle one, so they would win over it otherwise.
          "&, &:hover, &:active, &.Mui-disabled": {
            border: "1px solid",
            borderColor: "divider",
            color: "text.primary",
          },
          width: "100%",
          // Set as longhands so they win over the padding of the design system size variant.
          paddingTop: "8px",
          paddingBottom: "8px",
          paddingLeft: "11px",
          paddingRight: "11px",
          minHeight: "46px",
        }}
        {...rest}
      >
        {icon && label && (
          // `flex: 1` rather than a full width, because the loading spinner is rendered as
          // a sibling of this row inside the button: growing into the space that is left
          // keeps the row from overflowing, and the spinner from being squeezed, while loading.
          <Stack sx={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
            {icon}
            <Divider orientation="vertical" flexItem sx={{ mx: "10px" }} />
            <Text
              variant="labelM"
              component="p"
              sx={{
                textTransform: "none",
                textAlign: "center",
                width: "100%",
              }}
            >
              {label}
            </Text>
          </Stack>
        )}
        {icon && !label && icon}
      </Button>
      {description && (
        <Text
          variant="bodyXS"
          component="p"
          sx={{
            mt: "10px",
            textAlign: "center",
          }}
        >
          {description}
        </Text>
      )}
    </>
  );
}
